<script lang="ts">
    import {onMount} from "svelte";
    import {
        Autocomplete,
        Button,
        Divider,
        SnackbarContainer,
        TextField
    } from 'attractions';
    import chroma from 'chroma-js';
    import {CopyIcon, RefreshCwIcon} from 'svelte-feather-icons';
    import {HsvPicker} from 'svelte-color-picker';
    import Clipboard from "../lib/Clipboard";

    const namedColors = ["AliceBlue", "AntiqueWhite", "Aqua", "Aquamarine", "Azure", "Beige", "Bisque", "Black", "BlanchedAlmond", "Blue", "BlueViolet", "Brown", "BurlyWood", "CadetBlue", "Chartreuse", "Chocolate", "Coral", "CornflowerBlue", "Cornsilk", "Crimson", "Cyan", "DarkBlue", "DarkCyan", "DarkGoldenRod", "DarkGray", "DarkGrey", "DarkGreen", "DarkKhaki", "DarkMagenta", "DarkOliveGreen", "DarkOrange", "DarkOrchid", "DarkRed", "DarkSalmon", "DarkSeaGreen", "DarkSlateBlue", "DarkSlateGray", "DarkSlateGrey", "DarkTurquoise", "DarkViolet", "DeepPink", "DeepSkyBlue", "DimGray", "DimGrey", "DodgerBlue", "FireBrick", "FloralWhite", "ForestGreen", "Fuchsia", "Gainsboro", "GhostWhite", "Gold", "GoldenRod", "Gray", "Grey", "Green", "GreenYellow", "HoneyDew", "HotPink", "IndianRed", "Indigo", "Ivory", "Khaki", "Lavender", "LavenderBlush", "LawnGreen", "LemonChiffon", "LightBlue", "LightCoral", "LightCyan", "LightGoldenRodYellow", "LightGray", "LightGrey", "LightGreen", "LightPink", "LightSalmon", "LightSeaGreen", "LightSkyBlue", "LightSlateGray", "LightSlateGrey", "LightSteelBlue", "LightYellow", "Lime", "LimeGreen", "Linen", "Magenta", "Maroon", "MediumAquaMarine", "MediumBlue", "MediumOrchid", "MediumPurple", "MediumSeaGreen", "MediumSlateBlue", "MediumSpringGreen", "MediumTurquoise", "MediumVioletRed", "MidnightBlue", "MintCream", "MistyRose", "Moccasin", "NavajoWhite", "Navy", "OldLace", "Olive", "OliveDrab", "Orange", "OrangeRed", "Orchid", "PaleGoldenRod", "PaleGreen", "PaleTurquoise", "PaleVioletRed", "PapayaWhip", "PeachPuff", "Peru", "Pink", "Plum", "PowderBlue", "Purple", "RebeccaPurple", "Red", "RosyBrown", "RoyalBlue", "SaddleBrown", "Salmon", "SandyBrown", "SeaGreen", "SeaShell", "Sienna", "Silver", "SkyBlue", "SlateBlue", "SlateGray", "SlateGrey", "Snow", "SpringGreen", "SteelBlue", "Tan", "Teal", "Thistle", "Tomato", "Turquoise", "Violet", "Wheat", "White", "WhiteSmoke", "Yellow", "YellowGreen"
    ];
    let inputValue = "";
    let inputErrorMessage = "";
    let selection = [];
    let hexValue = "";

    let api = null;
    let values = [];
    let colorPreviewElement;
    let colorInputElement;


    async function* getOptions(text) {
        yield namedColors.map(c => {
            return {name: c}
        });
    }

    function valueUpdated(e = null) {
        if (e) {
            if (e.type && e.type === "colorChange") {
                inputValue = valueToString("rgba", [e.detail.r, e.detail.g, e.detail.b, e.detail.a]);
            } else if (e.currentTarget && e.currentTarget.value) {
                inputValue = e.currentTarget.value;
            }
        }
        try {
            inputErrorMessage = "";

                api = chroma(inputValue);


            hexValue = api.hex();
            const rgbaValue = valueToString('rgba', api.rgba());
            if (colorPreviewElement) {
                colorPreviewElement.style.backgroundColor = rgbaValue;
            }
            const valueKeys = ["hex", "rgba", "rgb", "hsl" /*, "hexa"*/];

            values = valueKeys.map(k => {
                const label = k;
                if(k === "hexa"){
                    k = "hex";
                }
                const value = valueToString(k, api[k]());
                return {
                    "label": label,
                    "value": value
                };

            });
        } catch (e) {
            inputErrorMessage = e;
        }
    }



    function valueToString(type, values) {
        if (type === "hex") {
            return values.substr(0,7);
        }

        if(type === "hsl") {
            return type + "(" +roundNumber(values[0]) + ", " + values.slice(1, 3).map(v => {
                return roundNumber(v * 100, 3)+"%";
            }).join(", ") + ")"
        }

        return type + "(" + values.map(v => {
            return roundNumber(v, 3);
        }).join(", ") + ")";
    }

    function roundNumber(num, scale = 0) {
        if(Number.isNaN(num)) {
            return 0;
        }

        if (!("" + num).includes("e")) {
            return +(Math.round(+(num + "e+" + scale)) + "e-" + scale);
        } else {
            const arr = ("" + num).split("e");
            let sig = ""
            if (+arr[1] + scale > 0) {
                sig = "+";
            }
            return +(Math.round(+(+arr[0] + "e" + sig + (+arr[1] + scale))) + "e-" + scale);
        }
    }

    onMount(() => {
        colorPreviewElement = document.getElementById('color-preview');
        colorInputElement = document.getElementById('color-input');
        inputValue = '#000000';
        valueUpdated();
    });


</script>
<style lang="scss">
  .preview-text {
    /* 1 pixel black shadow to left, top, right and bottom */
    text-shadow: -1px 0 black, 0 1px black, 1px 0 black, 0 -1px black;
     color: white;
  }

</style>
<h2>Colors</h2>
<SnackbarContainer let:showSnackbar>
    <form>
        <!--<Autocomplete {getOptions} selection={selection}/> -->
        <div class="text-field-shrunk text-field-shrunk--outline">
            <TextField outline error={inputErrorMessage} label="Input" bind:value={inputValue} on:input={valueUpdated} id="color-input"/>
        </div>
        <Divider/>
        <div class="text-field-shrunk text-field-shrunk--outline">
            {#each values as {label, value},i}
                <TextField outline label={label} readonly bind:value={value}/>
                <Button filled
                        on:click={() =>{ Clipboard.copyTextToClipboard(value); showSnackbar({ props: { text: `copied ${value} to clipboard.`  } })}}>
                    <CopyIcon size="20" class="mr"/>
                </Button>
            {/each}
        </div>
        <!--<Autocomplete outline {getOptions} maxOptions={1} minSearchLength={0} on:change={valueUpdated}/>-->


        <Divider/>
        <div class="color-picker-container">
            <HsvPicker on:colorChange={valueUpdated} startColor={'#000'}/>
            <div id="color-preview" class="color-picker-container__preview">
                Color preview
            </div>
        </div>

    </form>
</SnackbarContainer>
